import styled from '@emotion/styled';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridProps } from '@mui/material/Grid';
import { addSeconds, format } from 'date-fns';
import { MouseEvent as ReactMouseEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { constants } from '../../helpers';
import { hasPermissions } from '../../helpers/utls';
import {
  activeCountriesSelector,
  changeAutoUpdateTimer,
  dashboardAutoUpdateTimerSelector,
  filterParametersDataSelector,
  findAllRequest,
  userPermissionsDataSelector,
} from '../../state';
import Countdown from './Countdown';
import DashboardInsights from './DashboardInsights';
import Dashboards from './Dashboards';
import { EmptyState, PageHeader, SectionCard, StatusChip } from '../Visual';

const DASHBOARD_LAYOUT_KEY = 'dashboard-layout-order';
const DASHBOARD_HIDDEN_WIDGETS_KEY = 'dashboard-hidden-widgets';
const DASHBOARD_DENSITY_KEY = 'dashboard-density';
const DASHBOARD_WIDGET_SIZES_KEY = 'dashboard-widget-sizes';

type DashboardDensity = 'comfortable' | 'compact';
type DashboardWidgetSize = 'small' | 'medium' | 'large' | 'full';

interface DashboardWidgetDimensions {
  columns: number;
  height: number;
}

const DASHBOARD_WIDGET_MIN_COLUMNS = 3;
const DASHBOARD_WIDGET_MAX_COLUMNS = 12;
const DASHBOARD_WIDGET_DEFAULT_HEIGHT = 360;
const DASHBOARD_WIDGET_MIN_HEIGHT = 260;
const DASHBOARD_WIDGET_MAX_HEIGHT = 900;
const DASHBOARD_WIDGET_MAXIMIZED_HEIGHT = 720;
const DASHBOARD_WIDGET_COLUMN_STEP = 90;

const DashboardShell = styled(Box, {
  shouldForwardProp: prop => prop !== 'presentationMode' && prop !== 'density',
})<{ presentationMode: boolean; density: DashboardDensity }>(
  ({ theme, presentationMode, density }) => ({
    ...(presentationMode && {
      position: 'fixed',
      inset: 0,
      zIndex: theme.zIndex.modal + 1,
      overflow: 'auto',
      padding: theme.spacing(3),
      background: theme.palette.background.default,
    }),
    ...(density === 'compact' && {
      '.dashboard-widget-card .MuiCardContent-root': {
        padding: theme.spacing(1.5),
        '&:last-child': {
          paddingBottom: theme.spacing(1.5),
        },
      },
      '.dashboard-widget-card .MuiDivider-root': {
        marginBlock: theme.spacing(0.75),
      },
    }),
  })
);

const MainPannel = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'end',
});

const PannelContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  justifyContent: 'end',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 999,
  padding: theme.spacing(0.25, 0.75, 0.25, 1),
  boxShadow: 'none',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
    borderRadius: 18,
    flexDirection: 'column',
    whiteSpace: 'normal',
    width: '100%',
  },
}));

const PannelInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  opacity: 0.8,
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginInline: theme.spacing(0.5),
  color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : undefined,
}));

const WidgetSurface = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'isEditing' &&
    prop !== 'widgetHeight' &&
    prop !== 'isWidgetMaximized' &&
    prop !== 'hasMaximizedWidget',
})<{
  isEditing: boolean;
  widgetHeight?: number;
  isWidgetMaximized: boolean;
  hasMaximizedWidget: boolean;
}>(({ theme, isEditing, widgetHeight, isWidgetMaximized, hasMaximizedWidget }) => ({
  position: 'relative',
  height: widgetHeight ? `${widgetHeight}px` : '100%',
  minHeight: widgetHeight ? DASHBOARD_WIDGET_MIN_HEIGHT : undefined,
  outline: isEditing ? `2px dashed ${theme.palette.primary.light}` : 'none',
  outlineOffset: 6,
  borderRadius: theme.shape.borderRadius,
  transition: 'height 120ms ease, outline-color 160ms ease, opacity 180ms ease',
  ...(hasMaximizedWidget &&
    !isWidgetMaximized && {
      opacity: 0.12,
      pointerEvents: 'none',
      userSelect: 'none',
    }),
  ...(isWidgetMaximized && {
    position: 'fixed',
    inset: theme.spacing(3),
    zIndex: theme.zIndex.modal + 2,
    height: 'auto',
    minHeight: 'auto',
    maxHeight: `calc(100vh - ${theme.spacing(6)})`,
    width: 'auto',
    maxWidth: `calc(100vw - ${theme.spacing(6)})`,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: `0 24px 56px ${alpha(theme.palette.common.black, 0.45)}`,
  }),
  '& .dashboard-widget-card': {
    height: '100%',
  },
}));

const WidgetMaximizeBackdrop = styled(Box)(({ theme }) => ({
  position: 'fixed',
  inset: 0,
  zIndex: theme.zIndex.modal + 1,
  backgroundColor: alpha(theme.palette.common.black, 0.35),
  backdropFilter: 'blur(1px)',
}));

const DragHandle = styled(Box)(({ theme }) => {
  const accentColor =
    theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main;

  return {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 34,
    borderRadius: 999,
    color: accentColor,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `0 10px 24px ${alpha(theme.palette.text.primary, 0.12)}`,
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing',
    },
  };
});

const ResizeHandle = styled(Box)(({ theme }) => {
  const accentColor =
    theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main;

  return {
    position: 'absolute',
    right: theme.spacing(-1),
    bottom: theme.spacing(-1),
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 38,
    height: 38,
    borderRadius: 14,
    color: accentColor,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `0 10px 24px ${alpha(theme.palette.text.primary, 0.12)}`,
    cursor: 'nwse-resize',
    touchAction: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 7,
      bottom: 7,
      width: 12,
      height: 12,
      borderRight: `2px solid ${accentColor}`,
      borderBottom: `2px solid ${accentColor}`,
      borderRadius: 1,
    },
  };
});

interface SortableDashboardWidgetProps {
  id: string;
  children: ReactNode;
  gridProps?: GridProps;
  isEditing: boolean;
  isWidgetMaximized: boolean;
  hasMaximizedWidget: boolean;
  widgetHeight?: number;
  widgetLabel: string;
  onResizeStart: (event: ReactMouseEvent<HTMLElement>) => void;
  onToggleWidgetMaximize: () => void;
  maximizeTooltip: string;
  minimizeTooltip: string;
  maximizeAriaLabel: string;
  minimizeAriaLabel: string;
  resizeTooltip: string;
  resizeAriaLabel: string;
}

const MaximizeButton = styled(IconButton)(({ theme }) => {
  const accentColor =
    theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main;
  const hoverBackground =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.background.paper, 0.92)
      : alpha(theme.palette.background.paper, 0.98);

  return {
    position: 'absolute',
    top: theme.spacing(-1),
    right: theme.spacing(-1),
    zIndex: 2,
    width: 34,
    height: 34,
    color: accentColor,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
    boxShadow: `0 10px 24px ${alpha(theme.palette.text.primary, 0.12)}`,
    transition: 'background-color 140ms ease, box-shadow 160ms ease, border-color 140ms ease',
    '&:hover': {
      backgroundColor: hoverBackground,
      borderColor:
        theme.palette.mode === 'dark'
          ? alpha(theme.palette.secondary.main, 0.55)
          : alpha(theme.palette.primary.main, 0.45),
      boxShadow: `0 14px 30px ${alpha(theme.palette.text.primary, 0.2)}`,
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha(accentColor, 0.45)}`,
      outlineOffset: 1,
    },
  };
});

const SortableDashboardWidget = ({
  id,
  children,
  gridProps,
  isEditing,
  isWidgetMaximized,
  hasMaximizedWidget,
  widgetHeight,
  widgetLabel,
  onResizeStart,
  onToggleWidgetMaximize,
  maximizeTooltip,
  minimizeTooltip,
  maximizeAriaLabel,
  minimizeAriaLabel,
  resizeTooltip,
  resizeAriaLabel,
}: SortableDashboardWidgetProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !isEditing,
  });
  const style = {
    opacity: isDragging ? 0.72 : 1,
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Grid
      item
      {...gridProps}
      sx={{
        transition: 'flex-basis 160ms ease, max-width 160ms ease, opacity 180ms ease',
        ...(hasMaximizedWidget &&
          !isWidgetMaximized && {
            opacity: 0.55,
          }),
        ...gridProps?.sx,
      }}
    >
      <WidgetSurface
        ref={setNodeRef}
        style={style}
        isEditing={isEditing}
        isWidgetMaximized={isWidgetMaximized}
        hasMaximizedWidget={hasMaximizedWidget}
        widgetHeight={widgetHeight}
        data-dashboard-widget={id}
        data-tour="dashboard-widget-card"
        data-tour-widget-label={widgetLabel}
      >
        {!isEditing && (
          <Tooltip title={isWidgetMaximized ? minimizeTooltip : maximizeTooltip}>
            <MaximizeButton
              aria-label={isWidgetMaximized ? minimizeAriaLabel : maximizeAriaLabel}
              size="small"
              onClick={onToggleWidgetMaximize}
            >
              {isWidgetMaximized ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </MaximizeButton>
          </Tooltip>
        )}
        {isEditing && (
          <DragHandle {...attributes} {...listeners}>
            <DragIndicatorIcon fontSize="small" />
          </DragHandle>
        )}
        {isEditing && (
          <Tooltip title={resizeTooltip}>
            <ResizeHandle role="separator" aria-label={resizeAriaLabel} onMouseDown={onResizeStart}>
              <AspectRatioIcon
                sx={theme => ({
                  fontSize: theme.typography.subtitle1.fontSize,
                  transform: 'rotate(90deg)',
                })}
              />
            </ResizeHandle>
          </Tooltip>
        )}
        {children}
      </WidgetSurface>
    </Grid>
  );
};

function getFormattedTime(time: number) {
  const result = Math.round((time - new Date().getTime()) / 1000);
  return result;
}

function getDefaultWidgetColumns(gridProps?: GridProps) {
  return Number(gridProps?.lg || gridProps?.xl || gridProps?.md || 4);
}

function getGridPropsFromColumns(columns: number): GridProps {
  return {
    xs: 12,
    sm: 12,
    md: Math.min(Math.max(columns, 6), DASHBOARD_WIDGET_MAX_COLUMNS),
    lg: columns,
    xl: columns,
  };
}

function getClampedWidgetDimensions(columns: number, height: number): DashboardWidgetDimensions {
  return {
    columns: Math.min(
      Math.max(columns, DASHBOARD_WIDGET_MIN_COLUMNS),
      DASHBOARD_WIDGET_MAX_COLUMNS
    ),
    height: Math.min(Math.max(height, DASHBOARD_WIDGET_MIN_HEIGHT), DASHBOARD_WIDGET_MAX_HEIGHT),
  };
}

function getStoredWidgetDimensions() {
  const rawWidgetDimensions = JSON.parse(
    localStorage.getItem(DASHBOARD_WIDGET_SIZES_KEY) || '{}'
  ) as Record<string, DashboardWidgetDimensions | DashboardWidgetSize>;

  return Object.entries(rawWidgetDimensions).reduce<Record<string, DashboardWidgetDimensions>>(
    (result, [name, value]) => {
      if (typeof value === 'object' && value && 'columns' in value && 'height' in value) {
        result[name] = getClampedWidgetDimensions(value.columns, value.height);
      }
      return result;
    },
    {}
  );
}

const DashboardData = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const [initialDispatch, setInitialDispatch] = useState(false);
  const [isEditingLayout, setIsEditingLayout] = useState(false);
  const [presentationMode, setPresentationMode] = useState(false);
  const [maximizedWidget, setMaximizedWidget] = useState<string | null>(null);
  const [density, setDensity] = useState<DashboardDensity>(() => {
    const storedDensity = localStorage.getItem(DASHBOARD_DENSITY_KEY) as DashboardDensity | null;
    return storedDensity || 'comfortable';
  });
  const [hiddenWidgets, setHiddenWidgets] = useState<string[]>(() => {
    const storedHiddenWidgets = JSON.parse(
      localStorage.getItem(DASHBOARD_HIDDEN_WIDGETS_KEY) || '[]'
    ) as string[];
    return storedHiddenWidgets;
  });
  const [dashboardOrder, setDashboardOrder] = useState<string[]>(() => {
    const storedOrder = JSON.parse(localStorage.getItem(DASHBOARD_LAYOUT_KEY) || 'null') as
      | string[]
      | null;
    return storedOrder || Dashboards.map(dashboard => dashboard.name);
  });
  const [widgetDimensions, setWidgetDimensions] =
    useState<Record<string, DashboardWidgetDimensions>>(getStoredWidgetDimensions);
  const dashboardActionColor = theme.palette.mode === 'dark' ? 'secondary' : 'primary';

  const activeCountriesResource = useSelector(activeCountriesSelector);
  const userPermissionsData = useSelector(userPermissionsDataSelector, shallowEqual);
  const userPermissions = useMemo(
    () => userPermissionsData.map(i => i.permission),
    [userPermissionsData]
  );
  const autoUpdate = useSelector(dashboardAutoUpdateTimerSelector);
  const appParameters = useSelector(filterParametersDataSelector);
  const dashboardTimer = appParameters?.dashboardTimer || constants.defaultDashboardTimer;

  const [formattedTime, setFormattedTime] = useState(getFormattedTime(autoUpdate.time));

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const resources = Dashboards.map(i => useSelector(i.resource, shallowEqual));
  const resourcesByName = useMemo(
    () =>
      Dashboards.reduce<Record<string, (typeof resources)[number]>>((acc, dashboard, index) => {
        acc[dashboard.name] = resources[index];
        return acc;
      }, {}),
    [resources]
  );

  const accessibleDashboards = useMemo(
    () => Dashboards.filter(dashboard => hasPermissions(userPermissions, dashboard.permissions)),
    [userPermissions]
  );

  const orderedDashboards = useMemo(() => {
    const availableNames = accessibleDashboards.map(dashboard => dashboard.name);
    const orderedNames = [
      ...dashboardOrder.filter(name => availableNames.includes(name)),
      ...availableNames.filter(name => !dashboardOrder.includes(name)),
    ];
    return orderedNames
      .map(name => accessibleDashboards.find(dashboard => dashboard.name === name))
      .filter(Boolean) as typeof accessibleDashboards;
  }, [accessibleDashboards, dashboardOrder]);

  const visibleDashboards = useMemo(
    () => orderedDashboards.filter(dashboard => !hiddenWidgets.includes(dashboard.name)),
    [hiddenWidgets, orderedDashboards]
  );

  const hasMaximizedWidget = Boolean(maximizedWidget);

  const loading = useMemo(() => resources.some(i => i.loading), [resources]);
  const loaded = useMemo(() => resources.every(i => i.loaded), [resources]);

  const updateTime = (updateState?: boolean) => {
    if (updateState) {
      dispatch(changeAutoUpdateTimer({ ...autoUpdate, time: autoUpdate.time - 1 }));
    } else {
      setFormattedTime(prev => prev - 1);
    }
  };

  const handleRefresh = () => {
    const newTime = new Date().getTime() + dashboardTimer * 60000;
    dispatch(changeAutoUpdateTimer({ time: newTime, lastUpdate: new Date() }));
    setFormattedTime(getFormattedTime(newTime));
    dispatch(findAllRequest());
  };

  const handleResetLayout = () => {
    const defaultOrder = Dashboards.map(dashboard => dashboard.name);
    setDashboardOrder(defaultOrder);
    setHiddenWidgets([]);
    setDensity('comfortable');
    setWidgetDimensions({});
    localStorage.removeItem(DASHBOARD_LAYOUT_KEY);
    localStorage.removeItem(DASHBOARD_HIDDEN_WIDGETS_KEY);
    localStorage.removeItem(DASHBOARD_DENSITY_KEY);
    localStorage.removeItem(DASHBOARD_WIDGET_SIZES_KEY);
    setMaximizedWidget(null);
  };

  const handleDensityChange = () => {
    setDensity(currentDensity => {
      const nextDensity = currentDensity === 'comfortable' ? 'compact' : 'comfortable';
      localStorage.setItem(DASHBOARD_DENSITY_KEY, nextDensity);
      return nextDensity;
    });
  };

  const handleToggleWidget = (name: string) => {
    setHiddenWidgets(currentHiddenWidgets => {
      const isHidden = currentHiddenWidgets.includes(name);
      const nextHiddenWidgets = isHidden
        ? currentHiddenWidgets.filter(widgetName => widgetName !== name)
        : [...currentHiddenWidgets, name];
      const remainingVisibleWidgets = orderedDashboards.filter(
        dashboard => !nextHiddenWidgets.includes(dashboard.name)
      );
      if (remainingVisibleWidgets.length === 0) return currentHiddenWidgets;
      localStorage.setItem(DASHBOARD_HIDDEN_WIDGETS_KEY, JSON.stringify(nextHiddenWidgets));
      return nextHiddenWidgets;
    });
  };

  const handleDragEnd = ({
    active,
    over,
  }: {
    active: { id: string | number };
    over: { id: string | number } | null;
  }) => {
    if (!over || active.id === over.id) return;
    setDashboardOrder(currentOrder => {
      const visibleNames = visibleDashboards.map(dashboard => dashboard.name);
      const oldIndex = visibleNames.indexOf(active.id.toString());
      const newIndex = visibleNames.indexOf(over.id.toString());
      const reorderedVisibleNames = arrayMove(visibleNames, oldIndex, newIndex);
      const nextOrder = [
        ...reorderedVisibleNames,
        ...currentOrder.filter(name => !reorderedVisibleNames.includes(name)),
      ];
      localStorage.setItem(DASHBOARD_LAYOUT_KEY, JSON.stringify(nextOrder));
      return nextOrder;
    });
  };

  const handleResizeWidgetStart = (
    name: string,
    gridProps: GridProps | undefined,
    event: ReactMouseEvent<HTMLElement>
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const widgetElement = event.currentTarget.closest(
      '[data-dashboard-widget]'
    ) as HTMLElement | null;
    const currentDimensions = widgetDimensions[name];
    const initialColumns = currentDimensions?.columns || getDefaultWidgetColumns(gridProps);
    const initialHeight =
      currentDimensions?.height || widgetElement?.offsetHeight || DASHBOARD_WIDGET_DEFAULT_HEIGHT;
    const startX = event.clientX;
    const startY = event.clientY;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;

    document.body.style.cursor = 'nwse-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
      const deltaColumns = Math.round((moveEvent.clientX - startX) / DASHBOARD_WIDGET_COLUMN_STEP);
      const nextDimensions = getClampedWidgetDimensions(
        initialColumns + deltaColumns,
        initialHeight + moveEvent.clientY - startY
      );

      setWidgetDimensions(currentDimensionsByName => {
        const nextDimensionsByName = { ...currentDimensionsByName, [name]: nextDimensions };
        localStorage.setItem(DASHBOARD_WIDGET_SIZES_KEY, JSON.stringify(nextDimensionsByName));
        return nextDimensionsByName;
      });
    };

    const handleMouseUp = () => {
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const getWidgetGridProps = (name: string, gridProps?: GridProps) => {
    if (maximizedWidget === name) return getGridPropsFromColumns(DASHBOARD_WIDGET_MAX_COLUMNS);
    const dimensions = widgetDimensions[name];
    return dimensions ? getGridPropsFromColumns(dimensions.columns) : gridProps;
  };

  const getWidgetHeight = (name: string) => {
    const storedHeight = widgetDimensions[name]?.height;
    if (maximizedWidget === name) {
      return Math.max(
        storedHeight || DASHBOARD_WIDGET_DEFAULT_HEIGHT,
        DASHBOARD_WIDGET_MAXIMIZED_HEIGHT
      );
    }
    return storedHeight;
  };

  const getWidgetLabel = useMemo(
    () => (name: string) =>
      t(`dashboard.widgets.${name}`, {
        defaultValue: name.replaceAll('_', ' '),
      }),
    [t]
  );

  const renderDashboardControls = () => (
    <Grid item xs={12}>
      <Collapse in={isEditingLayout} timeout="auto" unmountOnExit>
        <SectionCard
          title={t('dashboard.layout.customization.title')}
          subtitle={t('dashboard.layout.customization.subtitle')}
          actions={
            <StatusChip
              status="info"
              label={t('dashboard.layout.widgetsVisible', {
                visible: visibleDashboards.length,
                total: orderedDashboards.length,
              })}
            />
          }
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {orderedDashboards.map(dashboard => (
                  <FormControlLabel
                    key={dashboard.name}
                    control={
                      <Checkbox
                        size="small"
                        checked={!hiddenWidgets.includes(dashboard.name)}
                        onChange={() => handleToggleWidget(dashboard.name)}
                      />
                    }
                    label={getWidgetLabel(dashboard.name)}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end' } }}>
                <Button
                  size="small"
                  variant="outlined"
                  color={dashboardActionColor}
                  startIcon={density === 'compact' ? <ViewComfyIcon /> : <ViewCompactIcon />}
                  onClick={handleDensityChange}
                >
                  {density === 'compact'
                    ? t('dashboard.layout.density.comfortable')
                    : t('dashboard.layout.density.compact')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color={dashboardActionColor}
                  startIcon={<RestartAltIcon />}
                  onClick={handleResetLayout}
                >
                  {t('navigation.buttons.restore')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </SectionCard>
      </Collapse>
    </Grid>
  );

  const renderPannel = (title: string, value: string) => (
    <PannelInfo marginLeft={2} marginY={1}>
      <Typography
        variant="caption"
        sx={theme => ({ fontWeight: theme.typography.caption.fontWeight, marginTop: '1px' })}
      >
        {title}
      </Typography>
      <Typography variant="caption" sx={{ marginLeft: 1, marginTop: '1px' }}>
        {value}
      </Typography>
    </PannelInfo>
  );

  const countdownTimer = (counter: number) => {
    const formattedTime = format(addSeconds(new Date(0), counter), 'mm:ss');
    return renderPannel(t('dashboard.nextUpdate'), formattedTime);
  };

  const handleToggleWidgetMaximize = (name: string) => {
    setMaximizedWidget(current => (current === name ? null : name));
  };

  useEffect(() => {
    if (
      maximizedWidget &&
      !visibleDashboards.some(dashboard => dashboard.name === maximizedWidget)
    ) {
      setMaximizedWidget(null);
    }
  }, [maximizedWidget, visibleDashboards]);

  useEffect(() => {
    if (activeCountriesResource.loaded && !loaded && !initialDispatch) {
      dispatch(findAllRequest());
      setInitialDispatch(true);
    }
  }, [activeCountriesResource, dispatch, loaded, initialDispatch]);

  return (
    <DashboardShell
      presentationMode={presentationMode}
      density={density}
      data-tour="dashboard-widgets"
    >
      <Grid container spacing={density === 'compact' ? 1.5 : 2}>
        <Grid item xs={12}>
          <PageHeader
            title={t('dashboard.title')}
            subtitle={t('shared.title')}
            eyebrow={t('dashboard.eyebrow')}
            actions={
              <>
                <MainPannel>
                  <PannelContent component={Paper}>
                    {renderPannel(
                      t('dashboard.lastUpdate'),
                      format(autoUpdate.lastUpdate, 'yyyy-MM-dd HH:mm:ss')
                    )}
                    <Countdown
                      time={formattedTime}
                      updateTime={updateTime}
                      onComplete={handleRefresh}
                      render={countdownTimer}
                    />
                    <StyledIconButton
                      aria-label={t('dashboard.actions.refresh')}
                      size="small"
                      onClick={handleRefresh}
                      disabled={loading}
                    >
                      {!loading && <RefreshIcon color="inherit" />}
                      {loading && <CircularProgress size={15} />}
                    </StyledIconButton>
                  </PannelContent>
                </MainPannel>
                <Box
                  sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                  data-tour="dashboard-controls"
                >
                  <Button
                    size="small"
                    variant="outlined"
                    color={dashboardActionColor}
                    startIcon={presentationMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
                    onClick={() => {
                      setMaximizedWidget(null);
                      setPresentationMode(value => !value);
                    }}
                  >
                    {presentationMode
                      ? t('dashboard.actions.exitPresentation')
                      : t('dashboard.actions.presentation')}
                  </Button>
                  <Button
                    size="small"
                    variant={isEditingLayout ? 'contained' : 'outlined'}
                    color={dashboardActionColor}
                    startIcon={isEditingLayout ? <SettingsIcon /> : <EditIcon />}
                    onClick={() => {
                      setMaximizedWidget(null);
                      setIsEditingLayout(value => !value);
                    }}
                  >
                    {isEditingLayout ? t('dashboard.layout.save') : t('dashboard.layout.customize')}
                  </Button>
                  {isEditingLayout && (
                    <Tooltip title={t('dashboard.layout.restoreOrder')}>
                      <Button
                        size="small"
                        variant="outlined"
                        color={dashboardActionColor}
                        startIcon={<RestartAltIcon />}
                        onClick={handleResetLayout}
                      >
                        {t('navigation.buttons.restore')}
                      </Button>
                    </Tooltip>
                  )}
                </Box>
              </>
            }
          />
        </Grid>
        {renderDashboardControls()}
        <Grid item xs={12}>
          <Box data-tour="dashboard-insights">
            <DashboardInsights
              salesResource={resourcesByName.SALES}
              collectionsResource={resourcesByName.COLLECTIONS}
              dailySalesResource={resourcesByName.DAILY_SALES}
            />
          </Box>
        </Grid>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={visibleDashboards.map(dashboard => dashboard.name)}
            strategy={rectSortingStrategy}
          >
            {visibleDashboards.map(dashboard => (
              <SortableDashboardWidget
                key={dashboard.name}
                id={dashboard.name}
                gridProps={getWidgetGridProps(dashboard.name, dashboard.gridProps)}
                isEditing={isEditingLayout}
                isWidgetMaximized={maximizedWidget === dashboard.name}
                hasMaximizedWidget={hasMaximizedWidget}
                widgetHeight={getWidgetHeight(dashboard.name)}
                widgetLabel={getWidgetLabel(dashboard.name)}
                maximizeTooltip={t('dashboard.layout.maximizeTooltip')}
                minimizeTooltip={t('dashboard.layout.minimizeTooltip')}
                maximizeAriaLabel={t('dashboard.layout.maximizeAria', {
                  widget: getWidgetLabel(dashboard.name),
                })}
                minimizeAriaLabel={t('dashboard.layout.minimizeAria', {
                  widget: getWidgetLabel(dashboard.name),
                })}
                resizeTooltip={t('dashboard.layout.resizeTooltip')}
                resizeAriaLabel={t('dashboard.layout.resizeAria', {
                  widget: getWidgetLabel(dashboard.name),
                })}
                onToggleWidgetMaximize={() => handleToggleWidgetMaximize(dashboard.name)}
                onResizeStart={event =>
                  handleResizeWidgetStart(dashboard.name, dashboard.gridProps, event)
                }
              >
                {dashboard.component}
              </SortableDashboardWidget>
            ))}
          </SortableContext>
        </DndContext>
        {hasMaximizedWidget && <WidgetMaximizeBackdrop onClick={() => setMaximizedWidget(null)} />}
        {visibleDashboards.length === 0 && (
          <Grid item xs={12}>
            <EmptyState
              title={t('dashboard.layout.empty.title')}
              description={t('dashboard.layout.empty.description')}
              actionLabel={t('dashboard.layout.empty.action')}
              onAction={handleResetLayout}
            />
          </Grid>
        )}
      </Grid>
    </DashboardShell>
  );
};

export default DashboardData;
